import { groupBy, sum } from 'lib/array/arrayUtils'
import { createSelector } from 'reselect'
import { getItemViewsWithStatusesAndBedGroups } from 'checkout/lib/utils/bedbank/view'
import { getFullOffers } from 'selectors/offerSelectors'
import { getIsLuxPlusLPPEnabled } from 'luxPlus/selectors/featureToggle'
import getBedbankItems from 'checkout/selectors/view/getBedbankItems'
import { isBedbankOffer } from 'lib/offer/offerTypes'
import { findPostPurchaseCheckout } from 'lib/checkout/checkoutUtils'

// Copies a lot of logic from this function: src/checkout/selectors/view/getBedbankItemViews.ts
// Idea around selector is that when building the request for payment schedule adjustments we need to exclude the current adjustment amounts from the calculation
const getBedbankChangeDatePriceDiff = createSelector(
  (state: App.State) => getBedbankItems(state),
  (state: App.State) => getFullOffers(state),
  (state: App.State) => state.offer.bedbankOfferRates,
  (state: App.State) => state.businessTraveller.offersCredits,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => state.checkout.sessions,
  // This is temporary until LPP member pricing is fully released
  (state: App.State) => getIsLuxPlusLPPEnabled(state),
  (
    cartItems,
    offers,
    bedbankRatesByOffer,
    offerCredits,
    postPurchase,
    existingOrder,
    sessions,
    luxPlusLPPEnabled,
  ): number => {
    // Each item = 1 room, but we want to process the same dates/offer as a single view
    const itemsByBookingKey = groupBy(cartItems, item => `${item.offerId}-${item.checkIn}-${item.checkOut}`)
    const itemGroups = itemsByBookingKey.values().map(items => ({
      checkIn: items[0].checkIn,
      checkOut: items[0].checkOut,
      offerId: items[0].offerId,
      items,
    })).toArray()

    const itemViews = itemGroups.map((itemGroup) => {
      const { offerId, checkIn, checkOut, items } = itemGroup
      const offer = offers[offerId]

      if (!isBedbankOffer(offer)) {
        return undefined
      }

      const itemsBySessionId = groupBy(items, (item) => item.sessionId ?? 'none')

      const itemViewsWithStatusesAndBedGroups = getItemViewsWithStatusesAndBedGroups(
        items,
        itemsBySessionId,
        bedbankRatesByOffer,
        offer,
        checkIn,
        checkOut,
        luxPlusLPPEnabled,
        sessions,
        offerCredits,
        postPurchase,
        existingOrder,
      )

      return itemViewsWithStatusesAndBedGroups
    })

    return itemViews.reduce((acc, itemView) => acc + sum(itemView?.map(i => i.itemViewsWithStatuses.data?.totals?.price ?? 0) ?? []), 0)
  },
)

export default getBedbankChangeDatePriceDiff
